import { useEffect, useMemo, useCallback } from "react";
import { useGetCountryEmissionFactorsQuery } from "../app/features/other/genericApiSlice";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../app/hooks";
import { selectCurrentProject } from "../app/features/project/projectSlice";
import { getIn, useFormikContext } from "formik";
import { Country } from "../types/interfaces";
import { useGetCountryByIdQuery } from "@hooks/index";

const CountryEmissionFactorChecker = ({
  country,
  onMissing,
}: {
  country: Country;
  onMissing: (country: Country) => void;
}) => {
  const { data } = useGetCountryEmissionFactorsQuery(country.id!, {
    skip: !country.id,
  });

  useEffect(() => {
    if (data && data.length === 0) {
      onMissing(country);
    }
  }, [data, country, onMissing]);

  return null;
};

export const MissingEmissionFactorForCountry = <T,>({
  handleDefaultsError,
  listInputName,
}: {
  handleDefaultsError: (message: string) => void;
  listInputName: string;
}) => {
  const { t } = useTranslation();
  const { project } = useAppSelector(selectCurrentProject);
  const { values } = useFormikContext<T>();

  const list = getIn(values, listInputName);

  const projectCountryId = useMemo(() => {
    if (!list || !Array.isArray(list)) return null;

    const countriesFromList: (Country | null)[] = list.map((item: { country_t2: Country | null }) => item.country_t2);
    const isAtLeastOneCountryNull = countriesFromList.some((country) => country === null);

    return isAtLeastOneCountryNull && project?.country ? project.country : null;
  }, [list, project?.country]);

  const { data: projectCountry } = useGetCountryByIdQuery(projectCountryId ?? 0, {
    skip: !projectCountryId,
  });

  const countries = useMemo(() => {
    if (!list || !Array.isArray(list)) return [];

    const countriesFromList: (Country | null)[] = list.map((item: { country_t2: Country | null }) => item.country_t2);

    const uniqueCountries = countriesFromList
      .filter((country): country is Country => !!country)
      .filter(
        (country, index, self) =>
          self.findIndex((c) => c && c.id === country.id) === index,
      );

    const result = [...uniqueCountries];

    if (projectCountry && !result.some((country) => country.id === projectCountry.id)) {
      result.push(projectCountry);
    }

    return result;
  }, [list, projectCountry]);

  const handleMissingEmissionFactorForCountry = useCallback(
    (country: Country) => {
      const errorKey = "validations.no_emission_factor_for_country";
      const defaultMessage = `No Operating Margin Emission Factor is available for ${country.name}. Please refine it below in the EF for selected Country section.`;
      const translatedError =
        t(errorKey, { countryName: country.name || "the selected country" }) ||
        defaultMessage;
      handleDefaultsError(translatedError);
    },
    [t, handleDefaultsError],
  );

  return (
    <>
      {countries.map((country) => (
        <CountryEmissionFactorChecker
          key={country.id}
          country={country}
          onMissing={handleMissingEmissionFactorForCountry}
        />
      ))}
    </>
  );
};
